import { sendRequest } from 'best-modules/vue-utils'
import { Query } from 'best-modules/utils'
import { PaginationList } from 'best-modules/components/server-table/types'
import {
  DocumentAgreement,
  DocumentAgreementDashboardResponse,
  DocumentAgreementFile,
  DocumentAgreementLog,
  DocumentAgreementParticipant,
  DocumentAgreementRoute,
} from '@/utils/types/documentAgreement'

type DocumentAgreementCredentials = Partial<{
  typeId: number
  name: string
  number: string
  document: unknown
  contractors: number[]
}>

type DocumentAgreementContractorCredentials = {
  contractorId: number
  documentId: number
}

type DocumentAgreementContractor = {
  id: number
  documentId: number
  contractorId: number
  contractor: {
    id: number
    name: string
    code: string
    shortName?: string | null
  }
}

type DocumentAgreementFileCredentials = {
  documentId: number
  name: string
  path: string
  size: number
}

type DocumentAgreementRelatedCredentials = {
  parentDocumentId: number
  childrenDocumentId: number
}

type DocumentAgreementRouteImportCredentials = {
  templateId: number
}

const documentAgreementListUrl = (): string => '/json/document/agreement'

const documentAgreementList = (
  query: Query = {}
): Promise<PaginationList<DocumentAgreement>> => {
  return sendRequest({
    name: 'documentAgreementList',
    method: 'get',
    url: {
      url: documentAgreementListUrl(),
      query,
    },
  })
}

const documentAgreementDashboard =
  (): Promise<DocumentAgreementDashboardResponse> => {
    return sendRequest({
      name: 'documentAgreementDashboard',
      method: 'get',
      url: '/json/document/agreement/dashboard',
    })
  }

const documentAgreementSingle = (
  documentAgreementId: number
): Promise<DocumentAgreement> => {
  return sendRequest({
    name: 'documentAgreementSingle',
    method: 'get',
    url: `/json/document/agreement/${documentAgreementId}`,
  })
}

const documentAgreementCreate = (
  credentials: DocumentAgreementCredentials
): Promise<DocumentAgreement> => {
  return sendRequest({
    name: 'documentAgreementCreate',
    method: 'post',
    url: '/json/document/agreement',
    payload: credentials,
  })
}

const documentAgreementUpdate = (
  documentAgreementId: number,
  credentials: DocumentAgreementCredentials
): Promise<DocumentAgreement> => {
  return sendRequest({
    name: 'documentAgreementUpdate',
    method: 'put',
    url: `/json/document/agreement/${documentAgreementId}`,
    payload: credentials,
  })
}

const documentAgreementDelete = (
  documentAgreementId: number
): Promise<void> => {
  return sendRequest({
    name: 'documentAgreementDelete',
    method: 'delete',
    url: `/json/document/agreement/${documentAgreementId}`,
  })
}

const documentAgreementFiles = (
  documentAgreementId: number
): Promise<DocumentAgreementFile[]> => {
  return sendRequest({
    name: 'documentAgreementFiles',
    method: 'get',
    url: `/json/document/agreement/${documentAgreementId}/files`,
  })
}

const documentAgreementFileCreate = (
  credentials: DocumentAgreementFileCredentials
): Promise<DocumentAgreementFile> => {
  return sendRequest({
    name: 'documentAgreementFileCreate',
    method: 'post',
    url: '/json/document/agreement/file',
    payload: credentials,
  })
}

const documentAgreementExtraFilesUpdate = (
  documentAgreementId: number,
  extraFiles: unknown
): Promise<DocumentAgreement> => {
  return sendRequest({
    name: 'documentAgreementExtraFilesUpdate',
    method: 'patch',
    url: `/json/document/agreement/${documentAgreementId}/extraAgreement`,
    payload: { extraFiles },
  })
}

const documentAgreementRoutes = (
  documentAgreementId: number
): Promise<DocumentAgreementRoute[]> => {
  return sendRequest({
    name: 'documentAgreementRoutes',
    method: 'get',
    url: `/json/document/agreement/${documentAgreementId}/routes`,
  })
}

const documentAgreementImportRoutes = (
  documentAgreementId: number,
  credentials: DocumentAgreementRouteImportCredentials
): Promise<DocumentAgreementRoute[]> => {
  return sendRequest({
    name: 'documentAgreementImportRoutes',
    method: 'patch',
    url: `/json/document/agreement/${documentAgreementId}/importRoutes`,
    payload: credentials,
  })
}

const documentAgreementParticipants = (
  documentAgreementId: number
): Promise<DocumentAgreementParticipant[]> => {
  return sendRequest({
    name: 'documentAgreementParticipants',
    method: 'get',
    url: `/json/document/agreement/${documentAgreementId}/participants`,
  })
}

const documentAgreementContractors = (
  documentAgreementId: number
): Promise<DocumentAgreementContractor[]> => {
  return sendRequest({
    name: 'documentAgreementContractors',
    method: 'get',
    url: `/json/document/agreement/${documentAgreementId}/contractors`,
  })
}

const documentAgreementContractorCreate = (
  credentials: DocumentAgreementContractorCredentials
): Promise<DocumentAgreementContractor> => {
  return sendRequest({
    name: 'documentAgreementContractorCreate',
    method: 'post',
    url: '/json/document/agreement/contractor',
    payload: credentials,
  })
}

const documentAgreementContractorDelete = (
  contractorId: number
): Promise<void> => {
  return sendRequest({
    name: 'documentAgreementContractorDelete',
    method: 'delete',
    url: `/json/document/agreement/contractor/${contractorId}`,
  })
}

const documentAgreementLogs = (
  documentAgreementId: number
): Promise<DocumentAgreementLog[]> => {
  return sendRequest({
    name: 'documentAgreementLogs',
    method: 'get',
    url: `/json/document/agreement/${documentAgreementId}/logs`,
  })
}

const documentAgreementRelatedDocuments = (
  documentAgreementId: number
): Promise<DocumentAgreement[]> => {
  return sendRequest({
    name: 'documentAgreementRelatedDocuments',
    method: 'get',
    url: `/json/document/agreement/${documentAgreementId}/relatedDocuments`,
  })
}

const documentAgreementRelatedDocumentCreate = (
  credentials: DocumentAgreementRelatedCredentials
): Promise<DocumentAgreement[]> => {
  return sendRequest({
    name: 'documentAgreementRelatedDocumentCreate',
    method: 'post',
    url: '/json/document/agreement/related',
    payload: credentials,
  })
}

const documentAgreementRelatedDocumentDelete = (
  credentials: DocumentAgreementRelatedCredentials
): Promise<DocumentAgreement[]> => {
  return sendRequest({
    name: 'documentAgreementRelatedDocumentDelete',
    method: 'delete',
    url: '/json/document/agreement/related',
    payload: credentials,
  })
}

const documentAgreementRelatedSignatures = (
  documentAgreementId: number
): Promise<unknown[]> => {
  return sendRequest({
    name: 'documentAgreementRelatedSignatures',
    method: 'get',
    url: `/json/document/agreement/${documentAgreementId}/relatedSignatures`,
  })
}

const documentAgreementApprove = (
  documentAgreementId: number
): Promise<DocumentAgreement> => {
  return sendRequest({
    name: 'documentAgreementApprove',
    method: 'patch',
    url: `/json/document/agreement/${documentAgreementId}/approve`,
  })
}

const documentAgreementRevision = (
  documentAgreementId: number,
  payload: object = {}
): Promise<DocumentAgreement> => {
  return sendRequest({
    name: 'documentAgreementRevision',
    method: 'patch',
    url: `/json/document/agreement/${documentAgreementId}/revision`,
    payload,
  })
}

const documentAgreementBreak = (
  documentAgreementId: number,
  payload: object = {}
): Promise<DocumentAgreement> => {
  return sendRequest({
    name: 'documentAgreementBreak',
    method: 'patch',
    url: `/json/document/agreement/${documentAgreementId}/break`,
    payload,
  })
}

const documentAgreementReturnFromRevision = (
  documentAgreementId: number,
  payload: object = {}
): Promise<DocumentAgreement> => {
  return sendRequest({
    name: 'documentAgreementReturnFromRevision',
    method: 'patch',
    url: `/json/document/agreement/${documentAgreementId}/returnFromRevision`,
    payload,
  })
}

const documentAgreementConvertToSignature = (
  documentAgreementId: number,
  payload: object = {}
): Promise<DocumentAgreement> => {
  return sendRequest({
    name: 'documentAgreementConvertToSignature',
    method: 'patch',
    url: `/json/document/agreement/${documentAgreementId}/convertToSignature`,
    payload,
  })
}

const documentAgreementRepeatSendApproved = (
  documentAgreementId: number
): Promise<DocumentAgreement> => {
  return sendRequest({
    name: 'documentAgreementRepeatSendApproved',
    method: 'patch',
    url: `/json/document/agreement/${documentAgreementId}/repeatSendApproved`,
  })
}

export {
  DocumentAgreementCredentials,
  DocumentAgreementContractor,
  DocumentAgreementContractorCredentials,
  documentAgreementListUrl,
  documentAgreementList,
  documentAgreementDashboard,
  documentAgreementSingle,
  documentAgreementCreate,
  documentAgreementUpdate,
  documentAgreementDelete,
  documentAgreementFiles,
  documentAgreementFileCreate,
  documentAgreementExtraFilesUpdate,
  documentAgreementRoutes,
  documentAgreementImportRoutes,
  documentAgreementParticipants,
  documentAgreementContractors,
  documentAgreementContractorCreate,
  documentAgreementContractorDelete,
  documentAgreementLogs,
  documentAgreementRelatedDocuments,
  documentAgreementRelatedDocumentCreate,
  documentAgreementRelatedDocumentDelete,
  documentAgreementRelatedSignatures,
  documentAgreementApprove,
  documentAgreementRevision,
  documentAgreementBreak,
  documentAgreementReturnFromRevision,
  documentAgreementConvertToSignature,
  documentAgreementRepeatSendApproved,
}
